import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Warning, HourglassMedium, Prohibit, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${window.location.origin}/api`;

const SECTIONS = [
  { key: "low_stock", title: "Low Stock", icon: Warning, tone: "text-red-700", border: "border-red-200", chip: "bg-red-100 text-red-700",
    row: (i) => [i.product_name, `${i.available_kg} / ${i.min_kg} KG`] },
  { key: "expiring", title: "Expiring Soon", icon: HourglassMedium, tone: "text-amber-700", border: "border-amber-200", chip: "bg-amber-100 text-amber-800",
    row: (i) => [`${i.product_name}${i.batch_code ? ` · ${i.batch_code}` : ""}`, `${i.days_left === 0 ? "Today" : `${i.days_left}d left`} · ${i.kg_remaining} KG`] },
  { key: "expired", title: "Expired", icon: Prohibit, tone: "text-gray-700", border: "border-gray-300", chip: "bg-gray-200 text-gray-700",
    row: (i) => [`${i.product_name}${i.batch_code ? ` · ${i.batch_code}` : ""}`, `${-i.days_left}d ago · ${i.kg_remaining} KG`] },
];

function AlertList({ section, items }) {
  const Icon = section.icon;
  return (
    <Card className={`p-3 border-2 ${section.border} bg-white`} data-testid={`stock-alert-${section.key}`}>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-sm font-bold flex items-center gap-1.5 ${section.tone}`}><Icon size={16} weight="bold" /> {section.title}</p>
        <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${section.chip}`} data-testid={`stock-alert-${section.key}-count`}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-4">Nothing here</p>
      ) : (
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {items.map((item, idx) => {
            const [main, side] = section.row(item);
            return (
              <div key={`${item.product_name}-${item.batch_code || idx}`} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-gray-50 text-xs">
                <span className="font-semibold text-gray-800 truncate">{main}</span>
                <span className="font-mono text-gray-600 flex-shrink-0">{side}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export function StockAlertsCard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { axios.get(`${API}/inventory/alerts`).then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return null;
  const total = data.counts.low_stock + data.counts.expiring + data.counts.expired;

  return (
    <div data-testid="stock-alerts-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base sm:text-lg font-black text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
          <span className="w-1 h-5 bg-red-500 rounded-full" />
          <Warning size={18} className="text-red-500" weight="bold" /> Stock Alerts
          {total > 0 && <span className="text-xs font-black text-white bg-red-500 rounded-full px-2 py-0.5" data-testid="stock-alerts-total">{total}</span>}
        </h2>
        <Button variant="ghost" size="sm" className="text-xs text-indigo-600" onClick={() => navigate("/inventory")} data-testid="stock-alerts-view-inventory">
          View Inventory <ArrowRight size={12} className="ml-1" />
        </Button>
      </div>
      {total === 0 ? (
        <Card className="p-4 border-2 border-green-200 bg-green-50 flex items-center gap-2 text-sm font-semibold text-green-700" data-testid="stock-alerts-healthy">
          <CheckCircle size={18} weight="bold" /> All stock levels and batch expiries look healthy
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SECTIONS.map((s) => <AlertList key={s.key} section={s} items={data[s.key]} />)}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        Low stock: below {data.settings.low_stock_kg} KG or the product's own minimum · Expiry window: {data.settings.expiry_days} days · Change in Settings
      </p>
    </div>
  );
}
